import { finishBoot, getBootSnapshot, recoverBoot } from './boot-budget.js';
import { ApiError } from '#~/api/types.js';

// Application entry owns the boot lifecycle. `main.tsx` calls
// `initializeBootBudget` before `createRoot`, so every React consumer below
// (Boundary, BootOverlay, Root) runs with the budget already installed and
// simply delegates to it. There is deliberately no second, storage-backed
// claim/terminal implementation here: two owners meant two sources of truth for
// "has this boot already settled", and the canonical boot_started /
// boot_succeeded / boot_failed rows are emitted by the budget's own sender,
// which `main.tsx` wires to the same `track(..., APP_LIFECYCLE)` path the
// trackers used.

/** Current launch terminal, if any. */
export const readBootLifecycleTerminal = (): 'succeeded' | 'failed' | null => {
  const outcome = getBootSnapshot()?.outcome;
  return outcome === 'succeeded' || outcome === 'failed' ? outcome : null;
};

/**
 * One publish-failure owner. Boundary, overlay stall and Root call this so a
 * consumed terminal slot always emits boot_failed.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global).
 */
export const publishBootLifecycleFailure = ({
  cause,
  errorType
}: {
  cause?: unknown;
  errorType?: AppBootErrorType;
}): boolean => finishBoot('failed', errorType ?? classifyAppBootErrorType(cause));

/** The committed readiness publisher and the deadline share one outcome. */
export function publishBootLifecycleSuccess(): boolean {
  return finishBoot('succeeded');
}

/** Canonical Retry: hand the current attempt back to the launch owner. */
export const retryCurrentDocumentBoot = (): void => {
  void recoverBoot('retry');
};

// ENG-4154 Phase 5: 'cancelled' distinguishes a deliberately aborted boot
// (AbortError — remount/teardown) from a real 'timeout'. Conflating the two
// inflated the timeout rate with benign cancellations. The app_boot_failed
// payload SHAPE is unchanged; only the classifier's vocabulary grew.
//
// ENG-4419: 'boot_timeout' is the watchdog-origin failure — the boot never
// reached a terminal within BOOT_DEADLINE_MS. It is DISTINCT from 'timeout'
// (reserved for transport timeouts: HTTP 408/504 and message-matched
// "timeout"/"deadline exceeded" below) so that a wedged boot with no thrown
// error is queryable separately from a slow-but-throwing network call. The
// watchdog feeds this value explicitly and never routes through
// classifyAppBootErrorType (there is no error object to classify).
export type AppBootErrorType =
  | 'network'
  | 'auth'
  | 'timeout'
  | 'boot_timeout'
  | 'cancelled'
  | 'unexpected';

const normalizeErrorMessage = (cause: unknown): string => {
  if (cause instanceof Error) {
    return `${cause.name} ${cause.message}`.trim().toLowerCase();
  }

  if (typeof cause === 'string') {
    return cause.toLowerCase();
  }

  return '';
};

const classifySingleBootError = (cause: unknown): AppBootErrorType => {
  if (cause instanceof ApiError) {
    if ([401, 403].includes(Number(cause.code))) {
      return 'auth';
    }

    if ([408, 504].includes(Number(cause.code))) {
      return 'timeout';
    }
  }

  // AbortError is a deliberate cancellation (remount/teardown), NOT a timeout.
  // Classify it first so it never falls into the timeout bucket below.
  if (cause instanceof Error && cause.name === 'AbortError') {
    return 'cancelled';
  }

  const message = normalizeErrorMessage(cause);

  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('deadline exceeded')
  ) {
    return 'timeout';
  }

  if (
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('auth') ||
    message.includes('token') ||
    message.includes('login required')
  ) {
    return 'auth';
  }

  if (
    message.includes('network') ||
    message.includes('internet connection') ||
    message.includes('api unreachable') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('offline') ||
    message.includes('err_network')
  ) {
    return 'network';
  }

  return 'unexpected';
};

// Bounds a pathological or cyclic Error.cause chain.
const MAX_BOOT_ERROR_CAUSE_DEPTH = 8;

/**
 * Classify a boot failure by the outermost error in its Error.cause chain that
 * identifies what went wrong. Boot wraps exhausted retries, for example
 * `Unable to load rooms after retries.`, around the transport failure it gave
 * up on. Reading only that wrapper reported a registry outage as `unexpected`,
 * which the boot_failed receipt contract (error_type `network`) forbids.
 */
export const classifyAppBootErrorType = (cause: unknown): AppBootErrorType => {
  const visited = new Set<unknown>();
  let current: unknown = cause;
  for (let depth = 0; depth < MAX_BOOT_ERROR_CAUSE_DEPTH; depth += 1) {
    const type = classifySingleBootError(current);
    if (type !== 'unexpected') return type;
    visited.add(current);
    let next: unknown;
    try {
      next = current instanceof Error ? current.cause : undefined;
    } catch {
      // A throwing cause accessor must not turn a boot failure into a hang:
      // trackBootFailed has already claimed its single send by this point.
      break;
    }
    if (next === undefined || next === null || visited.has(next)) break;
    current = next;
  }
  return 'unexpected';
};

/** User-facing boot failure copy — never the raw exception text. */
export const bootFailureCopy = (
  cause: unknown
): { readonly message: string; readonly detail: string } => {
  switch (classifyAppBootErrorType(cause)) {
    case 'auth':
      return {
        message: 'Your session expired.',
        detail: 'Please sign in again to continue.'
      };
    case 'network':
      return {
        message: 'Studio could not connect.',
        detail: 'Check your internet connection and try again.'
      };
    case 'timeout':
    case 'boot_timeout':
      return {
        message: 'Loading is taking longer than expected.',
        detail: 'The studio could not finish loading. Please try again.'
      };
    case 'cancelled':
      return {
        message: 'Loading was interrupted.',
        detail: 'Please try again.'
      };
    default:
      return {
        message: 'Studio could not finish loading.',
        detail: 'Please try again. If this keeps happening, restart the app.'
      };
  }
};
